import * as React from 'react';
import type { RouteProps } from 'react-router-dom';
import { Route, useLocation } from 'react-router-dom';

import { isAuthenticated } from '@/components/AuthProvider';
import { FullPageLazyLoadingFallback } from '@/components/Loaders/FullPageLazyLoadingFallback';
import { usePrefetchInitialData } from '@/hooks/query/usePrefetchInitialData';
import { useUserData } from '@/hooks/query/useUserData';
import { NeedlRedirect } from '@/router';
import { type AppType, APP_SOURCE_TYPE_CONFIG } from '@/utils/appTypes';

const AuthenticatedWrapperImpl = ({
  children,
}: {
  children: React.ReactNode;
}) => {
  usePrefetchInitialData();

  return <>{children}</>;
};

/**
 * UserProvider ensures user data is fetched before mounting child components.
 *
 * When a user signs up, they're added to Cognito (authenticated) but not yet in our database.
 * The backend middleware creates the user record on the first API request with a valid token.
 *
 * Without this provider, multiple simultaneous API calls from usePrefetchInitialData would
 * all trigger concurrent user creation attempts, causing unnecessary transaction conflicts
 * in the database (only one would succeed).
 *
 * By blocking children until useUserData() completes, we guarantee the user record exists
 * before any other API calls are made, preventing race conditions.
 */
const UserProvider = ({ children }: { children: React.ReactNode }) => {
  const { isLoading } = useUserData();

  if (isLoading) {
    return <FullPageLazyLoadingFallback />;
  }

  return <>{children}</>;
};

const AuthenticatedWrapper = React.memo(AuthenticatedWrapperImpl);

export const ProtectedRoute: React.FunctionComponent<ProtectedRoutePropsType> =
  function ({
    children,
    redirectRoute,
    redirectAppType,
    shouldPrefetchInitialData = true,
    ...rest
  }) {
    const [isUserAuthenticated, setIsUserAuthenticated] = React.useState<
      boolean | undefined
    >(undefined);
    const { pathname } = useLocation();

    React.useEffect(() => {
      async function Auth() {
        const isAuth = await isAuthenticated();
        setIsUserAuthenticated(isAuth);
      }
      Auth();
    }, [pathname]);

    React.useEffect(() => {
      if (redirectAppType) {
        localStorage.setItem('appType', redirectAppType);
      }

      return () => {
        if (redirectAppType && isUserAuthenticated) {
          localStorage.removeItem('appType');
        }
      };
    }, [redirectAppType, isUserAuthenticated]);

    if (isUserAuthenticated === undefined) {
      return <FullPageLazyLoadingFallback />;
    }

    return (
      <Route
        {...rest}
        render={() => {
          if (isUserAuthenticated) {
            return (
              <UserProvider>
                {shouldPrefetchInitialData ? (
                  <AuthenticatedWrapper>{children}</AuthenticatedWrapper>
                ) : (
                  children
                )}
              </UserProvider>
            );
          }

          // after aws logout -> aws will redirect to the / then go back to the original page of featured app
          const appType = localStorage.getItem('appType') as AppType;
          if (
            appType &&
            appType !== redirectAppType &&
            APP_SOURCE_TYPE_CONFIG[appType]
          ) {
            const redirectLocation =
              APP_SOURCE_TYPE_CONFIG[appType].afterLogout;
            localStorage.removeItem('appType');
            return <NeedlRedirect to={redirectLocation} />;
          }

          if (redirectRoute) {
            return (
              <NeedlRedirect
                to={{
                  pathname: redirectRoute,
                }}
              />
            );
          } else {
            window.location.href = import.meta.env.VITE_WEB_URL + 'auth';
          }
        }}
      />
    );
  };

type ProtectedRoutePropsType = RouteProps & {
  shouldPrefetchInitialData?: boolean;
  /**
   * for the new app types, we can be able to configure the default redirect route
   * Eg:- For Needl Pulse, when an unauthenticated user access a protected route page, we will redirect to Needl Pulse Sign In page (not the default app sign in page)
   */
  redirectRoute?: string;
  redirectAppType?: string;
};
